import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { AdminCapability, AdminRole } from '@kopechka/contracts';
import { resolveBaseUrl } from '../api/client.js';

export interface AdminUserInfo {
  readonly id: string;
  readonly username: string;
  readonly role: AdminRole;
  readonly capabilities: readonly AdminCapability[];
  readonly expiresAt: string;
  readonly sessionId: string;
}

export type AdmitState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'unauthenticated' }
  | { readonly kind: 'cookie_session'; readonly user: AdminUserInfo }
  | { readonly kind: 'expired' };

export interface SessionContextValue {
  readonly state: AdmitState;
  readonly role: AdminRole | null;
  readonly capabilities: readonly AdminCapability[];
  readonly actorId: string | null;
  readonly username: string | null;
  readonly expiresAt: Date | null;
  readonly origin: 'cookie_session' | null;
  readonly has: (cap: AdminCapability) => boolean;
  readonly logout: () => void;
  readonly signInWithPassword: (username: string, password: string) => Promise<
    | { readonly kind: 'ok'; readonly user: AdminUserInfo }
    | { readonly kind: 'invalid_credentials' }
    | { readonly kind: 'account_disabled'; readonly reason: string }
    | { readonly kind: 'error'; readonly reason: string }
  >;
}

const SessionContext = createContext<SessionContextValue | null>(null);

async function fetchMe(baseUrl: string): Promise<AdminUserInfo | null> {
  try {
    const resp = await fetch(`${baseUrl}/admin/auth/me`, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
    if (resp.status === 200) {
      return (await resp.json()) as AdminUserInfo;
    }
    return null;
  } catch {
    return null;
  }
}

export function SessionProvider({
  children,
}: {
  readonly children: ReactNode;
}): JSX.Element {
  const [state, setState] = useState<AdmitState>({ kind: 'loading' });
  const baseUrl = resolveBaseUrl();

  // On mount: restore session from cookie via /admin/auth/me
  useEffect(() => {
    let cancelled = false;
    void fetchMe(baseUrl).then((user) => {
      if (cancelled) return;
      if (user) {
        setState({ kind: 'cookie_session', user });
      } else {
        setState({ kind: 'unauthenticated' });
      }
    });
    return () => { cancelled = true; };
  }, [baseUrl]);

  // Auto-expire when the session expiry time is reached
  useEffect(() => {
    if (state.kind !== 'cookie_session') return;
    const expiresAt = new Date(state.user.expiresAt).getTime();
    const msLeft = expiresAt - Date.now();
    if (msLeft <= 0) {
      setState({ kind: 'expired' });
      return;
    }
    const t = window.setTimeout(() => setState({ kind: 'expired' }), msLeft);
    return () => window.clearTimeout(t);
  }, [state]);

  const signInWithPassword = useCallback(
    async (username: string, password: string) => {
      try {
        const resp = await fetch(`${baseUrl}/admin/auth/login`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ username, password }),
        });
        const data = (await resp.json()) as Record<string, unknown>;
        if (resp.status === 200) {
          const user = data as unknown as AdminUserInfo;
          setState({ kind: 'cookie_session', user });
          return { kind: 'ok' as const, user };
        }
        if (resp.status === 401) return { kind: 'invalid_credentials' as const };
        if (resp.status === 403) {
          return { kind: 'account_disabled' as const, reason: String(data['reason'] ?? 'account_disabled') };
        }
        return { kind: 'error' as const, reason: String(data['reason'] ?? `http_${resp.status}`) };
      } catch (err) {
        return { kind: 'error' as const, reason: err instanceof Error ? err.message : 'network_error' };
      }
    },
    [baseUrl],
  );

  const logout = useCallback(() => {
    void fetch(`${baseUrl}/admin/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    }).finally(() => {
      setState({ kind: 'unauthenticated' });
    });
  }, [baseUrl]);

  const value = useMemo<SessionContextValue>(() => {
    const derived = resolveDerived(state);
    return {
      state,
      ...derived,
      has: (cap) => derived.capabilities.includes(cap),
      logout,
      signInWithPassword,
    };
  }, [state, logout, signInWithPassword]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be called inside <SessionProvider>');
  return ctx;
}

function resolveDerived(state: AdmitState): {
  readonly role: AdminRole | null;
  readonly capabilities: readonly AdminCapability[];
  readonly actorId: string | null;
  readonly username: string | null;
  readonly expiresAt: Date | null;
  readonly origin: 'cookie_session' | null;
} {
  if (state.kind === 'cookie_session') {
    return {
      role: state.user.role,
      capabilities: state.user.capabilities,
      actorId: state.user.id,
      username: state.user.username,
      expiresAt: new Date(state.user.expiresAt),
      origin: 'cookie_session',
    };
  }
  return { role: null, capabilities: [], actorId: null, username: null, expiresAt: null, origin: null };
}
